const DEFAULT_POSITIONS = ['tor', 'abwehr', 'mittelfeld', 'sturm', 'tw'];

function isStatLine(line) {
    const t = String(line || '').trim();
    return t === '-' || /^-?\d+$/.test(t);
}

function buildPositionSet(positionNames = []) {
    const set = new Set(DEFAULT_POSITIONS);
    positionNames.forEach((name) => {
        if (name) set.add(String(name).toLowerCase());
    });
    return set;
}

function isPositionLine(line, positionSet) {
    return positionSet.has(String(line || '').trim().toLowerCase());
}

/**
 * Parse pasted squad list. Last numeric line per player = jersey number.
 */
export function parsePlayerList(text, positionNames = []) {
    const positionSet = buildPositionSet(positionNames);
    const lines = String(text || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

    const players = [];
    let i = 0;

    while (i < lines.length) {
        let name = lines[i];
        let position = null;
        i += 1;

        while (i < lines.length && !isStatLine(lines[i])) {
            const line = lines[i];
            if (line === name) {
                i += 1;
                continue;
            }
            if (isPositionLine(line, positionSet)) {
                position = line;
                i += 1;
                break;
            }
            name = line;
            i += 1;
        }

        const stats = [];
        while (i < lines.length && isStatLine(lines[i])) {
            stats.push(lines[i]);
            i += 1;
        }

        if (!stats.length) continue;

        const jerseyRaw = stats[stats.length - 1];
        const num = jerseyRaw === '-' ? null : parseInt(jerseyRaw, 10);

        players.push({
            fullName: name,
            position,
            num: Number.isFinite(num) ? num : null,
        });
    }

    return players;
}

export function normalizeName(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

export function splitFullName(fullName) {
    const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { prename: '', surname: '' };
    if (parts.length === 1) return { prename: '', surname: parts[0] };
    return {
        prename: parts.slice(0, -1).join(' '),
        surname: parts[parts.length - 1],
    };
}

export function findPlayerByName(players, fullName) {
    const target = normalizeName(fullName);
    if (!target) return null;

    const { prename, surname } = splitFullName(fullName);

    return players.find((p) => {
        const full = normalizeName(`${p.prename || ''} ${p.surname}`.trim());
        const reversed = normalizeName(`${p.surname} ${p.prename || ''}`.trim());
        if (full === target || reversed === target) return true;
        return normalizeName(p.prename) === normalizeName(prename)
            && normalizeName(p.surname) === normalizeName(surname);
    }) || null;
}

export function resolvePositionId(positionLabel, positions) {
    if (!positionLabel || !positions?.length) return null;
    const key = positionLabel.toLowerCase();
    const exact = positions.find((p) => p.name?.toLowerCase() === key);
    if (exact) return exact._id;
    const partial = positions.find((p) => {
        const name = p.name?.toLowerCase() || '';
        return name.startsWith(key) || key.startsWith(name);
    });
    return partial?._id || null;
}

export function buildImportPreview(parsedPlayers, existingPlayers, existingAssignments, positions) {
    const assignmentKey = (plid) => `${plid}`;

    const assignedPlids = new Set(
        (existingAssignments || []).map((a) => assignmentKey(a.plid?._id || a.plid))
    );

    return parsedPlayers.map((row) => {
        const { prename, surname } = splitFullName(row.fullName);
        const match = findPlayerByName(existingPlayers, row.fullName);
        const posid = resolvePositionId(row.position, positions);

        let status = 'new';
        let message = 'Spieler + Kader-Eintrag anlegen';

        if (match) {
            if (assignedPlids.has(assignmentKey(match._id))) {
                status = 'assigned';
                message = 'Bereits im Kader';
            } else {
                status = 'existing';
                message = 'Nur Kader-Eintrag anlegen';
            }
        }

        return {
            ...row,
            prename,
            surname,
            posid,
            matchedPlayer: match,
            status,
            message,
        };
    });
}
